import React, { useRef, useState } from 'react';
import { Card, Typography } from 'antd';

const { Text } = Typography;

interface FocusPointPickerProps {
  imageUrl: string;
  focusPoint: { x: number; y: number };
  onChange: (point: { x: number; y: number }) => void;
}

export const FocusPointPicker: React.FC<FocusPointPickerProps> = ({
  imageUrl,
  focusPoint,
  onChange,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    onChange({
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    });
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <Text style={{ fontSize: '11px', color: '#999' }}>
        Focus Point (click on image)
      </Text>
      <div
        ref={containerRef}
        onClick={handleClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          position: 'relative',
          width: '60%',
          aspectRatio: '9/16',
          marginTop: '4px',
          marginLeft: 'auto',
          marginRight: 'auto',
          cursor: 'crosshair',
          border: '2px solid #444',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: '#000',
        }}
      >
        <img
          src={imageUrl}
          alt="Panel"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isHovering ? 0.7 : 1,
            transition: 'opacity 0.2s',
          }}
        />
        
        {/* Focus point indicator */}
        <div
          style={{
            position: 'absolute',
            left: `${focusPoint.x * 100}%`,
            top: `${focusPoint.y * 100}%`,
            transform: 'translate(-50%, -50%)',
            width: '24px',
            height: '24px',
            pointerEvents: 'none',
          }}
        >
          {/* Outer circle */}
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: '2px solid #fff',
              boxShadow: '0 0 8px rgba(0,0,0,0.8)',
            }}
          />
          {/* Inner dot */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#1890ff',
              boxShadow: '0 0 4px rgba(24,144,255,0.8)',
            }}
          />
          {/* Crosshair */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '0',
            width: '100%',
            height: '1px',
            backgroundColor: '#fff',
            opacity: 0.5,
          }} />
          <div style={{
            position: 'absolute',
            top: '0',
            left: '50%',
            width: '1px',
            height: '100%',
            backgroundColor: '#fff',
            opacity: 0.5,
          }} />
        </div>
        
        {/* Coordinates display */}
        <div style={{
          position: 'absolute',
          bottom: '4px',
          right: '4px',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: '#fff',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '10px',
        }}>
          X: {Math.round(focusPoint.x * 100)}%, Y: {Math.round(focusPoint.y * 100)}%
        </div>
      </div>
    </div>
  );
};
